// Pricing catalog, 4 product tabs, each with plans + a granular feature-comparison matrix.
// Inspired by athennian.com/pricing (tabs + collapsible comparison table).
// Source references:
//   - CoSec App: cosecglobal.com/cosec-pricing/
//   - CoSearch: endole.co.uk/pricing/
//   - CoForm:   companiesmadesimple.com (2-tier simplification)
//   - CoVerify: PAYG credits (per-verification pricing)

export const PRICING_PRODUCTS = [
  {
    key: "cosec-app",
    label: "CoSec App",
    pageHref: "/cosecapp",
    blurb: "AI-powered UK company-secretarial software.",
    period: "month",
    periodNote: "billed annually",
    monthlyMarkupPct: 20, // monthly billing ~20% more than annual equivalent
    plans: [
      {
        key: "small",
        name: "Foundations",
        tagline: "For accountants and law practitioners starting their journey",
        annual: 35,
        entities: "Up to 25 entities",
        cta: "Book a Demo",
        href: "/contact",
        includes: [
          "Up to 25 entities",
          "Unlimited user licences",
          "Unlimited e-sign",
          "Unlimited storage",
          "GDPR compliant",
          "ISO 27001 certified",
        ],
      },
      {
        key: "medium",
        name: "Momentum",
        tagline: "For established and expanding accountancy practices",
        annual: 55,
        entities: "Up to 50 entities",
        cta: "Book a Demo",
        href: "/contact",
        featured: true,
        badge: "Most popular",
        includes: [
          "Up to 50 entities",
          "Unlimited user licences",
          "Unlimited e-sign",
          "Unlimited storage",
          "GDPR compliant",
          "ISO 27001 certified",
        ],
      },
      {
        key: "large",
        name: "Ascend",
        tagline: "For the practice set for inevitable achievement",
        annual: 80,
        entities: "Up to 100 entities",
        cta: "Book a Demo",
        href: "/contact",
        includes: [
          "Up to 100 entities",
          "Unlimited user licences",
          "Unlimited e-sign",
          "Unlimited storage",
          "GDPR compliant",
          "ISO 27001 certified",
        ],
      },
      {
        key: "xl",
        name: "Pinnacle",
        tagline: "For enterprise practices seeking greater success",
        annual: 150,
        entities: "Up to 300 entities",
        cta: "Contact sales",
        href: "/contact",
        includes: [
          "Up to 300 entities",
          "Unlimited user licences",
          "Unlimited e-sign",
          "Unlimited storage",
          "Dedicated account manager",
          "Premium support SLA",
        ],
      },
      {
        key: "corporate",
        name: "Corporate",
        tagline: "For in-house legal, FTSE-listed groups and multi-jurisdiction enterprises",
        annual: 599,
        entities: "Unlimited entities",
        cta: "Contact sales",
        href: "/contact",
        badge: "Enterprise",
        includes: [
          "Unlimited entities and users",
          "Private cloud deployment option",
          "99.9% uptime SLA",
          "Enterprise SSO (Azure AD, Okta, Ping)",
          "Audit log export and SIEM streaming",
          "Custom DPA, GDPR and ISO 27001 alignment",
          "Dedicated CSM and named technical contact",
          "24/7 priority support",
          "Multi-jurisdiction entity support (UK, EU, US)",
          "White-label branding and custom URL",
          "Quarterly business reviews",
        ],
      },
    ],
    // Feature-matrix groups (collapsible "Compare all features" section)
    matrix: [
      {
        group: "Service",
        rows: [
          { name: "Administrative fees", values: ["Unlimited", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
          { name: "Secure read-only users (client portal)", values: ["Unlimited", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
          { name: "Storage", values: ["Unlimited", "Unlimited", "Unlimited", "Unlimited", "Unlimited"] },
        ],
      },
      {
        group: "Entity data management",
        rows: [
          { name: "Address management", values: [true, true, true, true, true] },
          { name: "Registration & licence management", values: [true, true, true, true, true] },
          { name: "Audit trail", values: [true, true, true, true, true] },
          { name: "Template entities", values: [true, true, true, true, true] },
          { name: "Role-based & read-only reminders", values: [true, true, true, true, true] },
          { name: "Bulk import", values: [false, true, true, true, true] },
          { name: "Non-UK based entities", values: ["Coming soon", "Coming soon", "Coming soon", "Coming soon", true] },
        ],
      },
      {
        group: "Reminders & compliance",
        rows: [
          { name: "Date reminders", values: [true, true, true, true, true] },
          { name: "Task management & compliance dashboard", values: [true, true, true, true, true] },
        ],
      },
      {
        group: "Equity & ownership structure",
        rows: [
          { name: "Unlimited share classes", values: [true, true, true, true, true] },
          { name: "Amend share class terms", values: [true, true, true, true, true] },
          { name: "Split & consolidate shares", values: [true, true, true, true, true] },
          { name: "Stock certificate management", values: [true, true, true, true, true] },
          { name: "Multi-party structured shareholder types", values: [false, true, true, true, true] },
          { name: "Transaction & ownership ledgers", values: [true, true, true, true, true] },
          { name: "Share options", values: [false, true, true, true, true] },
          { name: "Org structure charts", values: [false, true, true, true, true] },
          { name: "Point-in-time org chart", values: [false, false, true, true, true] },
        ],
      },
      {
        group: "Document automation",
        rows: [
          { name: "Automate document templates & certificates", values: [true, true, true, true, true] },
          { name: "Automate board & shareholder consents", values: [false, true, true, true, true] },
          { name: "Automate formation documents", values: [false, true, true, true, true] },
          { name: "Multi-lingual document automation", values: [false, false, true, true, true] },
        ],
      },
      {
        group: "Reporting",
        rows: [
          { name: "Reports as Word, PDF or Excel", values: [true, true, true, true, true] },
          { name: "Custom reports", values: [false, true, true, true, true] },
          { name: "Entity summaries", values: [true, true, true, true, true] },
          { name: "Compliance event reports", values: [true, true, true, true, true] },
        ],
      },
      {
        group: "Integrations",
        rows: [
          { name: "Office 365", values: [true, true, true, true, true] },
          { name: "Adobe e-Signature", values: [true, true, true, true, true] },
          { name: "DocuSign", values: [false, true, true, true, true] },
          { name: "AWS integration", values: [false, false, true, true, true] },
        ],
      },
      {
        group: "IT & infrastructure",
        rows: [
          { name: "Google SSO", values: [true, true, true, true, true] },
          { name: "Enterprise SSO (Azure / Okta)", values: [false, false, true, true, true] },
          { name: "Unique domain URL", values: [false, false, true, true, true] },
          { name: "Co-label branding", values: [false, false, false, true, true] },
          { name: "Private cloud deployment", values: [false, false, false, false, true] },
          { name: "SIEM / audit log streaming", values: [false, false, false, false, true] },
        ],
      },
      {
        group: "Customer success",
        rows: [
          { name: "In-app live chat", values: [true, true, true, true, true] },
          { name: "Standard support SLA", values: [true, true, true, true, true] },
          { name: "Premium support SLA", values: [false, false, true, true, true] },
          { name: "99.9% uptime SLA", values: [false, false, false, false, true] },
          { name: "Dedicated account manager", values: [false, false, false, true, true] },
          { name: "Named technical contact", values: [false, false, false, false, true] },
          { name: "On-boarding support", values: [false, true, true, true, true] },
          { name: "Quarterly business reviews", values: [false, false, false, false, true] },
        ],
      },
    ],
  },
  {
    key: "coverify",
    label: "CoVerify",
    pageHref: "/coverify",
    blurb: "Buy CoVerify tokens and use them anywhere across the suite. £0.50 per token, with automatic volume discounts.",
    period: "token",
    periodNote: "no subscription required",
    // Calculator-driven product: pick a volume → see which tier applies + total cost.
    calculator: {
      minCredits: 5,
      defaultCredits: 50,
      basePricePerCredit: 0.50,
      tiers: [
        {
          key: "payg",
          name: "PAYG",
          min: 5,
          max: 99,
          discountPct: 0,
          tagline: "Pay-as-you-go, minimum 5 tokens, no commitment, no expiry.",
          cta: "Buy tokens",
          href: "/contact",
          includes: [
            "Minimum 5 tokens",
            "£0.50 / token (no discount)",
            "Biometric document + face match",
            "Liveness detection",
            "ECCTA-ready audit trail",
            "AML / sanctions / PEP screening",
          ],
        },
        {
          key: "saver",
          name: "Saver",
          min: 100,
          max: 499,
          discountPct: 10,
          tagline: "Saver tier kicks in at 100 tokens, 10% off the per-token price.",
          cta: "Buy Saver pack",
          href: "/contact",
          badge: "10% off",
          featured: true,
          includes: [
            "100 tokens or more",
            "£0.45 / token (10% discount)",
            "Everything in PAYG",
            "Bulk token dashboard",
            "Reusable across CoSec App + standalone",
            "Email & in-app reporting",
          ],
        },
        {
          key: "enterprise",
          name: "Enterprise",
          min: 500,
          max: null,
          discountPct: 15,
          // Custom-quote tier, calculator total + tier-card price are replaced by 'Let\'s talk'
          priceLabel: "Let's talk",
          tagline:
            "Integrate our market-leading compliance and fraud prevention platform to grow and govern at scale, with advanced features, controls, support, and commercial flexibility for global businesses.",
          cta: "Contact us",
          href: "/contact",
          badge: "Custom pricing",
          includes: [
            "eID authentication and global database checks",
            "Biometric enrolment and NFC chip verification",
            "Custom pricing and premium support",
            "Bespoke solutions co-built with experts",
            "Dedicated CSM + SLA",
            "Annual contract & committed-use discounts",
          ],
        },
      ],
    },
    matrix: [
      {
        group: "Verification methods",
        rows: [
          { name: "Document verification", values: [true, true, true] },
          { name: "Biometric face match", values: [true, true, true] },
          { name: "Passive liveness", values: [true, true, true] },
          { name: "Active liveness challenge", values: [false, true, true] },
          { name: "Proof of address", values: [false, true, true] },
        ],
      },
      {
        group: "Screening",
        rows: [
          { name: "AML / sanctions", values: [true, true, true] },
          { name: "PEP screening", values: [true, true, true] },
          { name: "Adverse media", values: [false, true, true] },
          { name: "Ongoing monitoring", values: [false, false, true] },
        ],
      },
      {
        group: "Integration",
        rows: [
          { name: "Hosted widget", values: [true, true, true] },
          { name: "Webhook callbacks", values: [true, true, true] },
          { name: "Direct API", values: [false, true, true] },
          { name: "Co-branded flow", values: [false, false, true] },
        ],
      },
      {
        group: "Commercials",
        rows: [
          { name: "Per-token price", values: ["£0.50", "£0.45", "Let's talk"] },
          { name: "Volume discount", values: ["-", "10%", "Custom"] },
          { name: "Minimum purchase", values: ["5 tokens", "100 tokens", "500+ tokens"] },
          { name: "Annual contract", values: [false, false, true] },
        ],
      },
    ],
  },
  {
    key: "cosearch",
    label: "CoSearch",
    pageHref: "/cosearch",
    blurb: "UK company intelligence, 5m+ companies, directors and PSCs.",
    period: "month",
    periodNote: "billed monthly",
    monthlyMarkupPct: 0,
    plans: [
      {
        key: "monthly",
        name: "Flex",
        tagline: "Access to rich business insights, cancel anytime",
        monthly: 39,
        annual: 39,
        cta: "Subscribe now",
        href: "/contact",
        includes: [
          "Unlimited company & director searches",
          "Unlimited company reports",
          "Export up to 1,000 rows monthly",
          "No contracts, cancel anytime",
          "Live credit scores & limits",
        ],
      },
      {
        key: "annual",
        name: "Always-On",
        tagline: "Best value, billed annually, one-off fee",
        monthly: 25,
        annual: 25,
        cta: "Subscribe now",
        href: "/contact",
        featured: true,
        badge: "Save 36%",
        includes: [
          "Everything in Monthly",
          "Export up to 15,000 rows yearly",
          "Annual access, one-off fee",
          "Priority email support",
          "Full credit-report library",
        ],
      },
      {
        key: "enterprise",
        name: "Command",
        tagline: "For demos, multi-user licences and higher volumes",
        priceLabel: "Custom",
        priceSub: "API + multi-seat",
        cta: "Contact sales",
        href: "/contact",
        includes: [
          "Multi-user licences",
          "Bulk export beyond 15k rows",
          "API access (REST)",
          "Custom data feeds",
          "Dedicated CSM",
        ],
      },
    ],
    matrix: [
      {
        group: "Data access",
        rows: [
          { name: "Unlimited company searches", values: [true, true, true] },
          { name: "Director profiles", values: [true, true, true] },
          { name: "10-year company accounts", values: [true, true, true] },
          { name: "Shareholders & group structures", values: [true, true, true] },
          { name: "Live credit scores & CCJs", values: [true, true, true] },
        ],
      },
      {
        group: "Export & lists",
        rows: [
          { name: "Row export per period", values: ["1,000 / mo", "15,000 / yr", "Unlimited"] },
          { name: "Saved lists", values: [true, true, true] },
          { name: "B2B marketing data", values: [true, true, true] },
          { name: "Bulk data feed", values: [false, false, true] },
        ],
      },
      {
        group: "Monitoring & alerts",
        rows: [
          { name: "Company monitoring", values: [true, true, true] },
          { name: "Custom alerts", values: [false, true, true] },
          { name: "API webhook feed", values: [false, false, true] },
        ],
      },
      {
        group: "Support",
        rows: [
          { name: "Email support", values: [true, true, true] },
          { name: "Priority support", values: [false, true, true] },
          { name: "Dedicated CSM", values: [false, false, true] },
        ],
      },
    ],
  },
  {
    key: "coform",
    label: "CoForm",
    pageHref: "/coform",
    blurb: "Form your UK limited company. Authorised Companies House agent.",
    period: "one-off",
    periodNote: "Companies House fee shown separately. Fees current at 1 Feb 2026.",
    plans: [
      {
        key: "basic",
        name: "Launch",
        tagline: "Fast, online UK limited company registration",
        priceLabel: "£24.99",
        priceSub: "+ £100 Companies House digital incorporation fee",
        cta: "Form your company",
        href: "/contact",
        includes: [
          "UK limited company registration",
          "Companies House submission",
          "Digital Certificate of Incorporation",
          "Memorandum & Articles of Association",
          "Share certificates (PDF)",
          "Web-filing authentication code",
        ],
      },
      {
        key: "plus-cosec",
        name: "Launch & Manage",
        tagline: "Form your company AND get a year of CoSec App Foundations",
        priceLabel: "£349.99",
        priceSub: "+ £100 Companies House fee. 1-yr subscription included.",
        cta: "Form & subscribe",
        href: "/contact",
        featured: true,
        badge: "Best value",
        includes: [
          "Everything in Basic Incorporation",
          "1 full year of CoSec App (Foundations plan)",
          "Up to 25 entities managed in CoSec App",
          "Unlimited user licences + e-sign",
          "Statutory registers + filings",
          "Priority on-boarding support",
        ],
      },
    ],
    matrix: [
      {
        group: "Incorporation",
        rows: [
          { name: "UK limited company registration", values: [true, true] },
          { name: "Digital Certificate of Incorporation", values: [true, true] },
          { name: "Memorandum & Articles of Association", values: [true, true] },
          { name: "Share certificates (PDF)", values: [true, true] },
          { name: "Web-filing authentication code", values: [true, true] },
        ],
      },
      {
        group: "Ongoing Cosec",
        rows: [
          { name: "1 year of CoSec App subscription", values: [false, true] },
          { name: "Statutory registers (digital)", values: [false, true] },
          { name: "Confirmation statement assistant", values: [false, true] },
          { name: "Director / PSC change filings", values: [false, true] },
          { name: "On-boarding support", values: [false, true] },
        ],
      },
      {
        group: "Add-ons",
        rows: [
          { name: "Registered office address (1 yr)", values: ["Add-on", "Add-on"] },
          { name: "Mail forwarding (1 yr)", values: ["Add-on", "Add-on"] },
          { name: "VAT registration assistance", values: ["Add-on", "Add-on"] },
          { name: "PAYE registration assistance", values: ["Add-on", "Add-on"] },
        ],
      },
    ],
  },
];

export const findProduct = (key) => PRICING_PRODUCTS.find((p) => p.key === key) || PRICING_PRODUCTS[0];
